// =====================================================
// API client — single fetch wrapper used by every call from the SPA.
//
// Responsibilities:
//   - prefix paths with /api (the host Caddy strips this before forwarding)
//   - attach the access JWT from sessionStore
//   - retry once on 401 by exchanging the refresh JWT for a fresh pair
//   - throw a structured ApiError so callers can inspect status + body
// =====================================================

import { sessionStore } from "../auth/session-store.js";
import { twoFactorGate } from "../auth/two-factor-gate.js";

const API_PREFIX = "/api";

export class ApiError extends Error {
  constructor({ status, statusText, body, url }) {
    super(
      `${status} ${statusText} on ${url}: ${
        typeof body === "string" ? body : JSON.stringify(body)
      }`,
    );
    this.name = "ApiError";
    this.status = status;
    this.statusText = statusText;
    this.body = body;
    this.url = url;
  }

  /** Pull the most useful human-readable message out of the body. */
  get displayMessage() {
    if (typeof this.body === "string") return this.body;
    if (this.body && typeof this.body === "object") {
      if (Array.isArray(this.body.message)) return this.body.message.join("\n");
      if (typeof this.body.message === "string") return this.body.message;
      if (typeof this.body.error === "string") return this.body.error;
    }
    return this.statusText || "خطای ناشناخته";
  }
}

const readBody = async (res) => {
  const ct = res.headers.get("content-type") || "";
  if (ct.includes("application/json")) {
    try {
      return await res.json();
    } catch {
      return null;
    }
  }
  try {
    return await res.text();
  } catch {
    return null;
  }
};

/**
 * Low-level request. Use the higher-level helpers in endpoints.js unless
 * you need full control.
 */
const rawFetch = async (method, path, { body, headers, accessToken } = {}) => {
  const url = path.startsWith("http") ? path : API_PREFIX + path;
  const init = {
    method,
    headers: {
      Accept: "application/json",
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...headers,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
    credentials: "same-origin",
  };
  const res = await fetch(url, init);
  if (!res.ok) {
    const body = await readBody(res);
    // S-2/S-3 — the backend's TwoFactorEnforcementGuard blocks a privileged-role
    // user who hasn't enabled 2FA with a 403 carrying this machine code. Raise
    // the forced-enrolment gate here (the single chokepoint every authenticated
    // call flows through) so ANY blocked request opens the overlay, then still
    // throw so the calling code path unwinds normally. Inert unless the backend
    // enforcement flag is ON (it never emits this code otherwise).
    if (res.status === 403 && body && typeof body === "object" && body.code === "two_factor_required") {
      twoFactorGate.require();
    }
    throw new ApiError({
      status: res.status,
      statusText: res.statusText,
      body,
      url,
    });
  }
  if (res.status === 204) return null;
  return readBody(res);
};

// Single-flight refresh: if 30 requests race a 401, we want exactly one
// refresh attempt and the rest to await its outcome.
let refreshInFlight = null;

const tryRefresh = async () => {
  if (refreshInFlight) return refreshInFlight;
  const session = sessionStore.get();
  // Logged in? The refresh token rides in the HttpOnly cookie (digiu_rt), which
  // the browser sends automatically (credentials: same-origin) — we no longer
  // hold it in JS. Gate on the access token instead of the (now absent) refresh.
  if (!session?.accessToken) return null;
  refreshInFlight = (async () => {
    try {
      const next = await rawFetch("POST", "/v1/auth/refresh", {
        // A legacy localStorage refresh token (from a pre-cookie session) is
        // forwarded once so the session migrates to the cookie; otherwise the
        // cookie alone authenticates the refresh.
        body: session.refreshToken ? { refreshToken: session.refreshToken } : undefined,
      });
      sessionStore.set(next);
      return next;
    } catch (err) {
      sessionStore.clear();
      throw err;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
};

// S7 — one-shot transient retry for idempotent GETs: a 502/503/504 (gateway
// blip while a deploy rolls a container / brief upstream restart) retries once
// after a short backoff before surfacing. Non-GETs never retry (not
// idempotent). Implemented as a wrapper around a fetch thunk — deliberately
// NOT by self-recursion, which sends tsc's cross-file inference into a cycle
// and degrades every call site to implicit any.
const TRANSIENT_STATUSES = new Set([502, 503, 504]);
const TRANSIENT_BACKOFF_MS = 400;
const withTransientRetry = async (doFetch, method) => {
  try {
    return await doFetch();
  } catch (err) {
    if (err instanceof ApiError && method === "GET" && TRANSIENT_STATUSES.has(err.status)) {
      await new Promise((r) => setTimeout(r, TRANSIENT_BACKOFF_MS));
      return doFetch();
    }
    throw err;
  }
};

/**
 * S-2/S-3 — force a refresh-token rotation and update the stored session.
 * Used by the forced-2FA-enrolment gate after the user enables 2FA, so the new
 * access token carries twoFactorEnabled=true and the guard stops blocking. Reuses
 * the single-flight tryRefresh (which updates sessionStore + clears on failure).
 * Returns the new session, or null when there is nothing to refresh.
 */
export const refreshSession = () => tryRefresh();

/**
 * Authenticated request — auto-refreshes on 401 once, then retries; a GET that
 * hits a transient gateway error (502/503/504) retries once after a backoff.
 */
export const apiFetch = async (method, path, opts = {}) => {
  const session = sessionStore.get();
  const accessToken = session?.accessToken;
  try {
    return await withTransientRetry(() => rawFetch(method, path, { ...opts, accessToken }), method);
  } catch (err) {
    if (!(err instanceof ApiError) || err.status !== 401 || opts._retried) {
      throw err;
    }
    let next;
    try {
      next = await tryRefresh();
    } catch {
      throw err;
    }
    if (!next?.accessToken) throw err;
    return rawFetch(method, path, {
      ...opts,
      _retried: true,
      accessToken: next.accessToken,
    });
  }
};

/**
 * Public (unauthenticated) request — used by login / register / health.
 * Same one-shot transient GET retry as apiFetch (S7).
 */
export const apiPublic = (method, path, opts = {}) =>
  withTransientRetry(() => rawFetch(method, path, opts), method);

export const api = {
  get: (path, opts) => apiFetch("GET", path, opts),
  post: (path, body, opts) => apiFetch("POST", path, { ...opts, body }),
  patch: (path, body, opts) => apiFetch("PATCH", path, { ...opts, body }),
  put: (path, body, opts) => apiFetch("PUT", path, { ...opts, body }),
  // `del` was the original short-form, but the R1 academicAdminApi
  // helpers (deleteSchool / deleteFaculty / etc.) were authored against
  // `api.delete` — which previously evaluated to undefined. The R1+R2
  // admin delete buttons were silently broken at the network layer
  // (the soft-delete buttons resolved to a TypeError in the browser
  // console; the server-side soft-delete never ran). Adding `delete`
  // as an alias here is the minimum-touch fix: keeps `api.del`
  // working for the legacy tutorApi.deleteSession + makes every
  // `api.delete(...)` call site (R1, R2, and R3.a's identityApi)
  // actually hit the backend. Discovered while authoring R3.a Commit I.
  del: (path, opts) => apiFetch("DELETE", path, opts),
  delete: (path, opts) => apiFetch("DELETE", path, opts),
};

export const publicApi = {
  post: (path, body, opts) => apiPublic("POST", path, { ...opts, body }),
  get: (path, opts) => apiPublic("GET", path, opts),
};
